import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ProfileMenu } from '../Profile/ProfileMenu';
import { IoArrowBack } from 'react-icons/io5';
import { useGetVotingPollQuery } from '@/store/api/votingApi';
import { MdCelebration } from 'react-icons/md';
import { buildAssetUrl } from '@/utils/asset';

const PlayerRow = ({ player, isWinner }: any) => {
  const contestant = player?.contestant || {};

  return (
    <div
      className={`flex flex-col lg:flex-row lg:items-center justify-between gap-4
      border border-[#1A2C4D] rounded-xl px-4 py-3 w-full
      ${isWinner ? 'bg-[#0F1F3D]' : 'bg-[#0A1426]'}`}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <img
          src={buildAssetUrl(contestant?.profileImage, '/images/gameSection/user.png')}
          className={`${isWinner ? 'w-20 h-20' : 'w-12 h-12'} rounded-full object-cover shrink-0`}
          onError={(e) => {
            e.currentTarget.src = '/images/gameSection/user.png';
          }}
        />

        <div className="flex flex-col">
          {isWinner && (
            <p className="flex items-center gap-2 text-lg text-[#518EFF] font-semibold font-irish">
              Winner
              <MdCelebration className="text-[#FF7A33] text-4xl" />
            </p>
          )}

          <p className="text-sm break-words leading-tight line-clamp-5">
            {contestant?.firstname} {contestant?.lastname}
          </p>

          <p className="text-xs sm:text-sm text-[#E4FFEF]/60">Total Votes: {player?.totalPoints}</p>
        </div>
      </div>

      {!isWinner && (
        <div className="px-3 py-1 rounded-md border text-sm border-[#1A2C4D] w-fit">Voted</div>
      )}
    </div>
  );
};

const RoundCard = ({ round }: any) => {
  const players = round?.results || [];
  const winners = players.filter((p: any) => p.isWinner);
  const heroWinner = winners[0];
  return (
    <div className="bg-[#010711] border border-[#1A2C4D] rounded-2xl overflow-hidden shadow-lg transition-transform duration-300 hover:-translate-y-2 flex flex-col h-full">
      <div className="relative w-full">
        <img
          src={buildAssetUrl(round?.roundBanner || heroWinner?.contestant?.profileImage, '/images/gameSection/user.png')}
          alt="Winner"
          className="w-full h-[220px] sm:h-[240px] md:h-[260px] object-cover rounded-t-2xl"
          onError={(e) => {
            e.currentTarget.src = '/images/gameSection/user.png';
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent rounded-t-2xl" />

        <div className="absolute bottom-6 left-4 text-white">
          <h3 className="text-xl md:text-2xl font-semibold">{round?.roundTitle}</h3>
          {/* {winners.length > 1 && (
            <p className="text-sm text-white/70">{winners.length} winners (tied)</p>
          )} */}
        </div>
      </div>

      <div
        className={`px-4 sm:px-6 py-4 max-h-[240px] overflow-y-auto thin-scrollbar scroll-smooth flex-1
        ${players.length === 0 ? 'flex items-center justify-center' : 'space-y-4'}`}
      >
        {players.length === 0 ? (
          <p className="text-white/50 text-center">No players</p>
        ) : (
          <>
            {winners.map((player: any) => (
              <PlayerRow key={`winner-${player?.contestant?.id}`} player={{ ...player }} isWinner />
            ))}

            {players
              .filter((p: any) => !p.isWinner)
              .map((player: any) => (
                <PlayerRow key={player?.contestant?.id} player={{ ...player }} />
              ))}
          </>
        )}
      </div>
    </div>
  );
};

interface VotingResultsProps {
  close: () => void;
  sessionId: number;
}

export default function RoundVoting({ close, sessionId }: VotingResultsProps) {
  const navigate = useNavigate();

  const { data } = useGetVotingPollQuery(sessionId);

  const rounds =
    data?.polls?.map((poll: any) => {
      const sorted = [...poll.pollContestants].sort(
        (a, b) => (b.totalVotePower || 0) - (a.totalVotePower || 0)
      );

      return {
        roundId: poll.id,
        roundTitle: poll.title,
        roundBanner: poll.banner,

        totalPoints: sorted.reduce((sum: number, pc: any) => sum + (pc.totalVotePower || 0), 0),

        results: sorted.map((pc: any, index: number) => ({
          contestant: pc.contestant,
          totalPoints: pc.totalVotePower,
          isWinner: pc.isWinner,
        })),
      };
    }) || [];

  const isSingle = rounds.length === 1;

  return (
    <div className="min-h-screen bg-[#05071A] text-white relative overflow-hidden">
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#FF5900] opacity-20 blur-[200px]" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#2C3FC2] opacity-20 blur-[220px]" />

      <header className="relative w-full border-b border-white/10 px-4 sm:px-6 md:px-12 py-4 flex items-center justify-between">
        <img
          src="/images/logo.svg"
          className="max-w-[150px] sm:max-w-[180px] md:max-w-[230px] cursor-pointer"
          onClick={() => navigate('/')}
        />
        <ProfileMenu />
      </header>

      <button
        onClick={close}
        className="flex items-center gap-2 text-white px-4 py-2 ml-4 mt-4 rounded-lg hover:bg-white/10 -translate-y-2"
      >
        <IoArrowBack size={24} />
        Back
      </button>

      {/* Title */}
      <div className="text-center mt-12 md:mt-16 mb-12 md:mb-16 px-4">
        <h1 className="text-lg sm:text-3xl md:text-4xl font-irish">ROUND RESULTS</h1>
      </div>

      <div
        className={`max-w-[1500px] z-10 px-4 mx-auto grid gap-6 md:gap-8 lg:gap-10 pb-20 ${
          isSingle ? 'grid-cols-1 place-items-center' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
        }`}
      >
        {rounds.map((round: any) => (
          <RoundCard key={round.roundId} round={round} />
        ))}
      </div>
    </div>
  );
}
